import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import React from "react";

import styled from "styled-components/macro";

import { useArchiveBoard, useDeleteBoard } from "Boards/APIReactQuery/queries";
import { reportsKeys } from "Reports/SavedReports/API/keys";

import FnrButton3, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import { FnrPopover } from "UI/FnrPopover2";
import FnrSlides from "UI/FnrSlides";

import useBoardLocation from "../hooks/useBoardLocation";

import DeleteConfirmation from "./DeleteConfirmation";

DeleteOrArchivePopover.propTypes = {
  setShowPopover: PropTypes.func.isRequired,
  buttonRef: PropTypes.object.isRequired,
  activeElement: PropTypes.string,
  setShowDeleteConfirmation: PropTypes.func,
  isArchived: PropTypes.bool.isRequired,
  boardID: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.number.isRequired,
  ]),
  groupURL: PropTypes.string,
};

export default function DeleteOrArchivePopover({
  setShowPopover,
  buttonRef,
  activeElement,
  setShowDeleteConfirmation,
  isArchived,
  boardID,
  groupURL,
}) {
  const queryClient = useQueryClient();
  const { isReportBoard } = useBoardLocation();

  const {
    mutate: deleteBoard,
    isLoading: isDeleting,
    error: deleteError,
    reset: resetDeleteState,
  } = useDeleteBoard({
    boardID,
    onSuccess: () => {
      if (isReportBoard) {
        queryClient.invalidateQueries(reportsKeys.getReportsList());
        window.fnReactBus.goToUrl("/reports");
      } else {
        window.location = "/" + groupURL;
      }
    },
  });

  const {
    mutate: archive,
    isLoading: isArchiving,
    isSuccess,
    reset: resetArchiveState,
    isError: archiveError,
  } = useArchiveBoard({
    boardID,
    onSuccess: () => {
      if (isArchived) {
        setTimeout(() => {
          setShowPopover(false);
          resetArchiveState();
        }, 1500);
        return;
      }
      window.fnReactBus.goToUrl("/reports");
    },
  });

  const archiveBoard = () => {
    archive({ boardID, archive: true });
  };

  const unarchiveBoard = () => {
    archive({ boardID, archive: false });
  };

  const onShouldClose = () => {
    setShowDeleteConfirmation(false);
    setShowPopover(false);
    resetArchiveState();
    resetDeleteState();
  };
  // We changed "boards" to "reports" in the tooltip text, regression-less change here
  const boardNoun = isReportBoard ? "report" : "report";
  const tooltipContent = `${
    isArchived ? "Unarchive" : "Archive"
  } or delete this ${boardNoun}`;

  return (
    <FnrPopover
      show={Boolean(activeElement)}
      onShouldClose={onShouldClose}
      buttonRef={buttonRef}
      color={activeElement === "delete" ? "red" : "blue"}
      offsetLeft={-100}
      style={{ width: "320px" }}
    >
      <FnrSlides
        clip={false}
        activeKey={activeElement}
        fullWidth
        components={[
          {
            key: "tooltip",
            value: () => <TooltipContent>{tooltipContent}</TooltipContent>,
          },
          {
            key: "popover",
            value: () => {
              return (
                <PopoverContent>
                  <div>
                    Would you like to
                    {isArchived ? " unarchive" : " archive"} or delete this
                    report?
                  </div>
                  <SpaceBetween>
                    <FnrButton3
                      onClick={() => setShowDeleteConfirmation(true)}
                      loading={isDeleting}
                      color="red"
                    >
                      <ButtonIcon fas fa-trash-alt />
                      <ButtonText>Delete</ButtonText>
                    </FnrButton3>

                    <FnrButton3
                      onClick={isArchived ? unarchiveBoard : archiveBoard}
                      color="gray"
                      loading={isArchiving}
                    >
                      {isSuccess ? (
                        <i className="fa fa-check-circle" />
                      ) : (
                        <>
                          <ButtonIcon fas fa-archive />
                          <ButtonText>
                            {isArchived ? "Unarchive" : "Archive"}
                          </ButtonText>
                        </>
                      )}
                    </FnrButton3>
                  </SpaceBetween>
                  <Error
                    deleteError={deleteError}
                    archiveError={archiveError}
                    boardNoun={boardNoun}
                  />
                </PopoverContent>
              );
            },
          },
          {
            key: "delete",
            value: () => (
              <PopoverContent>
                <DeleteConfirmation
                  isDeleting={isDeleting}
                  isArchiving={isArchiving}
                  onDelete={() => deleteBoard({ groupURL: null })}
                  onCancel={() => setShowDeleteConfirmation(false)}
                />
              </PopoverContent>
            ),
          },
        ]}
      />
    </FnrPopover>
  );
}

const Error = ({ deleteError, archiveError, boardNoun }) => {
  if (!deleteError && !archiveError) return null;

  let getMessage = () => {
    if (deleteError)
      return `Hmm. Something when wrong deleting this ${boardNoun}. Please try again.`;
    if (archiveError)
      return `Hmm. Something when wrong archiving this ${boardNoun}. Please try again.`;
  };

  let message = getMessage();

  return (
    <ErrorDiv>
      <i className="fad fa-exclamation-triangle" />
      <div>{message}</div>
    </ErrorDiv>
  );
};

const PopoverContent = styled.div`
  display: grid;
  gap: 1rem;
  padding: 0.5rem;
`;

const TooltipContent = styled.div`
  text-align: center;
`;

const ErrorDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f7f0b6;
  color: #d5a223;
  padding: 0.25rem;
  border-radius: 8px;
`;

const P = styled.p`
  margin: 0;
`;

const SpaceBetween = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;
